import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';

/**
 * 二级库消耗确认与成本转移（`/api/wl/consumption`，后端 CostTransferService）：
 * ① 配送签收自动按科室写 `wl_cost_transfer`（trigger=SIGN_AUTO）；
 * ② 科室消耗确认（手工/扫码）扣二级库库存并写成本转移（trigger=MANUAL）；
 * ③ 成本转移单可在「按消耗出账」时被结算单占用（settleId）。
 */
export interface CostConsumeItemInput {
  materialId: Id;
  materialCode?: string | null;
  materialName?: string | null;
  spec?: string | null;
  unitId: Id;
  unitName?: string | null;
  batchNo?: string | null;
  qty: number;
  /** 单价（不带则后端按库存成本价回落）。 */
  price: number;
}

export interface CostConsumeRequest {
  /** 消耗出库仓（二级库，必填）。 */
  warehouseId: Id;
  /** 消耗科室；0 时取当前用户科室。 */
  deptId: Id;
  deptName?: string | null;
  supplierId?: Id;
  /** 核算期间 yyyy-MM，缺省当前月。 */
  accountPeriod?: string | null;
  remark?: string | null;
  items: CostConsumeItemInput[];
}

export interface CostTransferRow {
  id: Id;
  bizNo: string;
  orgId: Id;
  sourceWarehouseId: Id;
  costDeptId: Id;
  costDeptName: string | null;
  costCenterCode: string | null;
  transferType: string | null;
  /** SIGN_AUTO 签收自动 / MANUAL 手工消耗 / 扫码。 */
  triggerSource: string | null;
  accountPeriod: string | null;
  transferStatus: string;
  totalQty: number;
  totalAmount: number;
  supplierId: Id;
  sourceBizNo: string | null;
  settleId: Id;
  bizDate: string;
  remark: string | null;
  itemCount: number;
}

export interface CostTransferItemRow {
  id: Id;
  transferId: Id;
  materialId: Id;
  materialCode: string | null;
  materialName: string | null;
  unitId: Id;
  qty: number;
  unitCost: number;
  amount: number;
  batchNo: string | null;
}

export interface CostTransferDetail {
  header: CostTransferRow;
  items: CostTransferItemRow[];
}

/** 消耗确认返回：新生成的 CB 单摘要（后端 ConsumeAsync 的匿名对象）。 */
export interface CostConsumeResult {
  id: Id;
  bizNo: string;
  deptId: Id;
  totalQty: number;
  totalAmount: number;
  status: string;
}

export interface CostTransferQuery {
  /** 核算期间 yyyy-MM。 */
  period?: string;
  deptId?: Id;
  status?: string;
  supplierId?: Id;
  /** 后端上限 200，缺省 100。 */
  size?: number;
}

export function createConsumptionApi(http: HttpClient) {
  return {
    list(query: CostTransferQuery = {}): Promise<CostTransferRow[]> {
      return http.request<CostTransferRow[]>({ path: '/wl/consumption', query: toQuery(query) });
    },

    detail(id: Id): Promise<CostTransferDetail> {
      return http.request<CostTransferDetail>({ path: `/wl/consumption/${id}` });
    },

    /** 消耗确认：扣二级库库存并写成本转移单（CB）。 */
    consume(body: CostConsumeRequest): Promise<CostConsumeResult> {
      return http.request<CostConsumeResult>({ path: '/wl/consumption', method: 'POST', body });
    }
  };
}

export type ConsumptionApi = ReturnType<typeof createConsumptionApi>;
